import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, map, switchMap, of } from 'rxjs';
import { ChoresService } from './chores.service';
import { ChildrenService } from './children.service';
import { Chore } from '../interfaces/chore.interface';
import { Child } from '../interfaces/child.interface';

@Injectable({
  providedIn: 'root'
})
export class ScreenSaverService {
  private readonly INACTIVITY_TIMEOUT = 300000; // 5 minutes in milliseconds
  private inactivityTimer: any;
  private events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
  private isActiveSubject = new BehaviorSubject<boolean>(false);
  public isActive$ = this.isActiveSubject.asObservable();
  private currentChoreIndex = 0;
  private choreRotationInterval: any;

  constructor(
    private ngZone: NgZone,
    private choresService: ChoresService,
    private childrenService: ChildrenService
  ) {}

  /**
   * Start monitoring user activity for screen saver
   */
  startMonitoring(): void {
    this.resetTimer();
    this.addEventListeners();
  }

  /**
   * Stop monitoring user activity
   */
  stopMonitoring(): void {
    this.clearTimers();
    this.removeEventListeners();
    this.deactivateScreenSaver();
  }

  /**
   * Manually activate the screen saver
   */
  activateScreenSaver(): void {
    this.isActiveSubject.next(true);
    this.startChoreRotation();
  }

  /**
   * Deactivate the screen saver
   */
  deactivateScreenSaver(): void {
    this.isActiveSubject.next(false);
    this.clearChoreRotation();
    this.resetTimer();
  }

  /**
   * Get incomplete chores with assigned children
   * Only fetches data when screen saver is active to save resources
   */
  getIncompleteChoresWithChildren(): Observable<Array<{chore: Chore, child: Child}>> {
    // Start with the isActive$ stream to control data fetching
    return this.isActive$.pipe(
      // Only continue with the data fetch when screen saver is active
      map(isActive => {
        if (!isActive) {
          // Return empty array when screen saver is not active
          return [] as Array<{chore: Chore, child: Child}>;
        }
        
        // Only fetch data when screen saver is active
        return combineLatest([
          this.choresService.getChores(),
          this.childrenService.getChildren()
        ]).pipe(
          map(([chores, children]) => {
            // Filter out completed chores
            const incompleteChores = chores.filter(chore => !chore.completedAt);
            
            // Map chores to their assigned children
            return incompleteChores
              .filter(chore => chore.assignedTo) // Only include chores assigned to a specific child
              .map(chore => {
                const assignedChild = children.find(child => child.id === chore.assignedTo);
                if (assignedChild) {
                  return { chore, child: assignedChild };
                }
                return null;
              })
              .filter(item => item !== null) as Array<{chore: Chore, child: Child}>;
          })
        );
      }),
      // SwitchMap to flatten the nested Observable
      // When isActive is false, this will emit the empty array
      // When isActive is true, this will subscribe to the inner Observable
      // and emit its values
      switchMap(dataObsOrArray => {
        if (Array.isArray(dataObsOrArray)) {
          return of(dataObsOrArray);
        }
        return dataObsOrArray;
      })
    );
  }

  /**
   * Reset the inactivity timer
   */
  private resetTimer(): void {
    this.clearTimers();
    
    // Run outside Angular zone for better performance
    this.ngZone.runOutsideAngular(() => {
      this.inactivityTimer = setTimeout(() => {
        // When timeout occurs, run back inside Angular zone
        this.ngZone.run(() => {
          this.activateScreenSaver();
        });
      }, this.INACTIVITY_TIMEOUT);
    });
  }

  /**
   * Clear all timers
   */
  private clearTimers(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
  }

  /**
   * Add event listeners to track user activity
   */
  private addEventListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      this.events.forEach(event => {
        window.addEventListener(event, this.resetTimerBound);
      });
    });
  }

  /**
   * Remove event listeners
   */
  private removeEventListeners(): void {
    this.events.forEach(event => {
      window.removeEventListener(event, this.resetTimerBound);
    });
  }

  /**
   * Bound version of resetTimer to use with event listeners
   */
  private resetTimerBound = () => {
    // If screen saver is active, deactivate it on user activity
    if (this.isActiveSubject.getValue()) {
      this.ngZone.run(() => {
        this.deactivateScreenSaver();
      });
    } else {
      this.resetTimer();
    }
  };

  /**
   * Start rotating through chores in the screen saver
   */
  private startChoreRotation(): void {
    this.clearChoreRotation();
    
    // Rotate chores every 10 seconds
    this.choreRotationInterval = setInterval(() => {
      this.currentChoreIndex++;
      // The actual chore display is handled by the component
    }, 10000);
  }

  /**
   * Clear the chore rotation interval
   */
  private clearChoreRotation(): void {
    if (this.choreRotationInterval) {
      clearInterval(this.choreRotationInterval);
    }
  }
}
